import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

type ThemePreference = 'light' | 'dark' | 'system';

interface ThemeContextValue {
  /** What the user picked. 'system' follows OS. */
  preference: ThemePreference;
  /** What we actually rendered (light or dark) — useful for icons. */
  resolved: 'light' | 'dark';
  cycle: () => void;
  set: (next: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'theme';

function readPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' ? v : 'system';
}

function resolve(pref: ThemePreference): 'light' | 'dark' {
  if (pref !== 'system') return pref;
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

// Header background per theme — kept in sync with `--color-background`
// in src/styles/globals.css (light: slate-50, dark: deep slate). The
// installed PWA's status bar / safe-area inset is painted with the
// <meta name="theme-color"> colour, and our dark mode is class-driven
// (not OS-driven), so a static `media="(prefers-color-scheme: dark)"`
// pair can't track it — we update the meta content on every theme flip.
const HEADER_BG: Record<'light' | 'dark', string> = {
  light: '#f8fafc',
  dark: '#0b1220',
};

function applyThemeColorMeta(theme: 'light' | 'dark') {
  if (typeof document === 'undefined') return;
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', HEADER_BG[theme]);
}

function applyClass(theme: 'light' | 'dark') {
  const root = document.documentElement;
  if (theme === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  applyThemeColorMeta(theme);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreference] = useState<ThemePreference>(() => readPreference());
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => resolve(readPreference()));

  // Apply class + listen to OS changes when in 'system' mode.
  useEffect(() => {
    const next = resolve(preference);
    setResolved(next);
    applyClass(next);

    if (preference !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const r = resolve('system');
      setResolved(r);
      applyClass(r);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [preference]);

  const set = useCallback((next: ThemePreference) => {
    setPreference(next);
    if (next === 'system') window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const cycle = useCallback(() => {
    set(preference === 'light' ? 'dark' : preference === 'dark' ? 'system' : 'light');
  }, [preference, set]);

  return (
    <ThemeContext.Provider value={{ preference, resolved, cycle, set }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be inside ThemeProvider');
  return ctx;
}
